import { useState } from 'react';
import clsx from 'clsx';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Button,
  Divider,
  makeStyles,
  Radio,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { colorSet } from '@components/Provider';
import {
  CapaBankCode,
  CapaPaymentMethodType,
  CreatePurchaseOrderInput,
  useFetchMyRefundBankAccountsQuery
} from '@generated/graphql';
import RefundAccount from './RefundAccount';
import { CreateOrderFormSection } from '../styles';
import { ACCOUNT_TRANSFER_ICON, CARD_ICON } from '../../assets';
import { BankAccountInputGroup } from '@routes/MyAccount/components/BankAccount/BankAccountInputGroup';
import { CustomizedTypography, Stack, VStack } from '@components/Customized';
import { RadioButton, RadioButtonChecked } from '@carbon/icons-react';

export interface PaymentInput {
  pay_method: CapaPaymentMethodType;
  bank?: CapaBankCode;
  bankAccount?: string;
}

const PaymentMethod = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const { control, errors } = useFormContext<
    CreatePurchaseOrderInput & PaymentInput
  >();
  const [method, setMethod] = useState<CapaPaymentMethodType | null>(null);
  const {
    container,
    options,
    marginDivider,
    inputGroup,
    radioButton
  } = useStyles();
  const { data } = useFetchMyRefundBankAccountsQuery({
    fetchPolicy: 'cache-first'
  });
  const ResponsiveStack = matches ? VStack : Stack;
  const isCard = method === CapaPaymentMethodType.CreditCard;
  const isAccountTransfer = method === CapaPaymentMethodType.VirtualAccount;
  const refundAccountList = data?.fetchMyRefundBankAccounts?.data;
  const refundAccount = refundAccountList?.[0];
  const errorMessage = errors?.pay_method?.message;

  return (
    <div className={container}>
      <CustomizedTypography variant="h4" fontWeight="500">
        결제 방식
      </CustomizedTypography>

      <Controller
        rules={{
          required: {
            value: true,
            message: '결제방식을 선택해주세요'
          }
        }}
        defaultValue={''}
        name="pay_method"
        control={control}
        render={({ onChange, value }) => {
          return (
            <div className={options}>
              <Box
                className={clsx({
                  option: true,
                  isSelected: isCard
                })}
                component={Button}
                onClick={() => {
                  setMethod(CapaPaymentMethodType.CreditCard);
                  onChange(CapaPaymentMethodType.CreditCard);
                }}
              >
                <Radio
                  size="small"
                  className={radioButton}
                  checked={value === CapaPaymentMethodType.CreditCard}
                  icon={<RadioButton />}
                  checkedIcon={<RadioButtonChecked />}
                />
                <img src={CARD_ICON} width={50} />
                <CustomizedTypography variant="body2">
                  신용/체크카드
                </CustomizedTypography>
              </Box>
              <Box
                className={clsx({
                  option: true,
                  isSelected: isAccountTransfer
                })}
                component={Button}
                onClick={() => {
                  setMethod(CapaPaymentMethodType.VirtualAccount);
                  onChange(CapaPaymentMethodType.VirtualAccount);
                }}
              >
                <Radio
                  size="small"
                  className={radioButton}
                  checked={value === CapaPaymentMethodType.VirtualAccount}
                  icon={<RadioButton />}
                  checkedIcon={<RadioButtonChecked />}
                />
                <img src={ACCOUNT_TRANSFER_ICON} width={55} />
                <CustomizedTypography variant="body2">
                  무통장입금
                </CustomizedTypography>
              </Box>
            </div>
          );
        }}
      />
      {errorMessage ? (
        <Stack justifyContent="flex-end">
          <CustomizedTypography variant="caption" color="error">
            {errorMessage}
          </CustomizedTypography>
        </Stack>
      ) : null}
      {/* 결제수단 설명 */}
      <div
        className={clsx({
          'account-transfer-info': true,
          isAccountTransfer,
          noRefundAccount: !refundAccount
        })}
      >
        <CustomizedTypography
          variant="body2"
          color="textPrimary"
          gutterBottoms={2}
        >
          무통장입금 안내
        </CustomizedTypography>
        <CustomizedTypography variant="subtitle2" color="textSecondary">
          발주서를 발행한 후 고객님의 가상계좌로 입금해주세요. 캐파 가상계좌에
          입금 완료가 확인된 후 파트너와 제조를 시작할 수 있습니다. 24시간
          이내에 입금하지 않으면 발주서가 자동 취소됩니다.
        </CustomizedTypography>

        <Divider className={marginDivider} />
        <CreateOrderFormSection>
          <CustomizedTypography
            variant="h4"
            gutterBottoms={8}
            fontWeight="500"
            color="textPrimary"
          >
            환불계좌
          </CustomizedTypography>

          <ResponsiveStack className={inputGroup} gap={12}>
            {isAccountTransfer &&
              (refundAccount ? (
                <RefundAccount refundAccount={refundAccount} />
              ) : (
                <BankAccountInputGroup control={control} errors={errors} />
              ))}
          </ResponsiveStack>
        </CreateOrderFormSection>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '& .account-transfer-info': {
      maxHeight: 0,
      overflow: 'hidden',
      opacity: 0,
      transition: 'all 0.5s',
      '&.isAccountTransfer': {
        maxHeight: '247px',
        height: 'auto',
        opacity: 1,
        '&.noRefundAccount': {
          maxHeight: '560px'
        }
      }
    }
  },
  options: {
    display: 'flex',
    gap: '8px',
    '& .option': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '147px',
      borderRadius: '8px',
      boxShadow: `inset 0 0 0 1px ${colorSet.gray400}`,
      padding: '26px 0px',
      '& .MuiButton-label': {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 24
      },
      '&:hover': {
        boxShadow: `inset 0 0 0 2px ${colorSet.gray900}`,
        backgroundColor: colorSet.gray000
      },
      '&.isSelected': {
        boxShadow: `inset 0 0 0 2px ${colorSet.gray900}`,
        backgroundColor: colorSet.gray100
      }
    }
  },
  marginDivider: {
    margin: '32px 0px'
  },
  inputGroup: {
    '& > label': {
      flex: 1
    }
  },
  radioButton: {
    color: colorSet.gray1000,
    position: 'absolute',
    top: 15,
    left: 15,
    padding: 0,
    '&.MuiRadio-colorSecondary.Mui-checked': {
      color: colorSet.gray1000
    }
  }
}));

export default PaymentMethod;
